export const CHECKOUT_URL = 'https://resin-en.impultienda.ar/checkout/'

export const heroImage =
  'https://cdn.magicpatterns.com/uploads/5PSNSBDogjGM5Xv5mxKPm8/Diseno_sin_titulo-3.png'

export const authorImage =
  'https://cdn.magicpatterns.com/uploads/esLqQSGd37KWpT3onDR3nJ/Dra._Karito_Odontologa_OF.png'

export const stats = [
  { value: '40+', label: 'Clinical matrices, protocols & tools' },
  { value: '6', label: 'Premium clinical volumes' },
  { value: '5', label: 'Professional bonuses' },
  { value: '<15 min', label: 'To organize your case planning' },
]

export const beforeItems = [
  'You spend too much time searching for information before starting.',
  'You are unsure which material properties matter for a specific case.',
  'You struggle to visualize your layering strategy.',
  'You approach each anterior case differently.',
  'Your information is scattered across courses, notes, screenshots and PDFs.',
  'You know the theory — but have no fast system for turning it into decisions.',
]

export const afterItems = [
  'You assess the case using a structured framework.',
  'You organize your decisions with visual matrices.',
  'You plan your layering strategy before starting.',
  'You have dedicated playbooks for common anterior situations.',
  'You use practical checklists during case preparation.',
  'You have a repeatable framework for every anterior composite case.',
]

export const painPoints = [
  {
    icon: '😰',
    text: 'You have an anterior case in front of you and know what you’re doing — but you’re not completely sure where to start.',
  },
  {
    icon: '⏱️',
    text: 'You spend too much time reviewing notes, courses or product information before deciding how to approach the case.',
  },
  {
    icon: '🤷',
    text: 'You still hesitate when thinking about value, opacity, translucency, shade and layering.',
  },
  {
    icon: '🦷',
    text: 'You find it difficult to visualize the final layering strategy before you begin.',
  },
  {
    icon: '💰',
    text: 'You know anterior aesthetic dentistry can represent valuable treatment opportunities, but you want a more structured way to plan.',
  },
  {
    icon: '😓',
    text: 'After finishing a case, you sometimes notice details you wish you had anticipated beforehand.',
  },
]

export type Volume = {
  number: string
  title: string
  promise: string
  description: string
  insideLabel: string
  inside: string[]
  footnote?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume I',
    title: 'The Resin Architect Method™',
    promise: 'Turn an anterior case into a visual decision map',
    description:
      'The core framework for analyzing an anterior case before you begin and organizing the key decisions involved in treatment planning.',
    insideLabel: 'Inside',
    inside: [
      'Case classification',
      'Initial assessment',
      'Restorative objectives',
      'Decision framework',
      'Key variables',
      'Pre-case planning',
      'Pre-operative checklist',
    ],
  },
  {
    number: 'Volume II',
    title: 'Resin Selection Matrix™',
    promise: 'Identify the material properties you need before choosing your composite',
    description:
      'A visual framework for organizing the characteristics of the case and identifying the optical and handling properties you need to consider.',
    insideLabel: 'Inside',
    inside: [
      'Composite selection framework',
      'Opacity',
      'Translucency',
      'Value',
      'Chroma',
      'Optical properties',
      'Comparison matrices',
      'Material selection checklist',
    ],
  },
  {
    number: 'Volume III',
    title: 'Layering Architecture™',
    promise: 'Build your layering strategy before you touch the tooth',
    description:
      'A visual framework for understanding how different layers contribute to the final appearance of an anterior composite restoration.',
    insideLabel: 'Inside',
    inside: [
      'Layering architecture',
      'Dentin layer',
      'Enamel layer',
      'Effects',
      'Translucency',
      'Volume management',
      'Layer distribution',
      'Visual planning',
      'Common planning mistakes',
    ],
  },
  {
    number: 'Volume IV',
    title: 'Anterior Anatomy Blueprint™',
    promise: 'Create anterior anatomy with a clear visual reference',
    description: 'A practical visual system covering the anatomy that makes a restoration read as natural.',
    insideLabel: 'Covering',
    inside: [
      'Proportions',
      'Contours',
      'Line angles',
      'Lobes',
      'Ridges',
      'Mamelons',
      'Texture',
      'Macro-anatomy',
      'Micro-anatomy',
      'Surface morphology',
      'Light reflection',
    ],
  },
  {
    number: 'Volume V',
    title: 'Anterior Case Playbooks™',
    promise: 'Stop starting from scratch with every case',
    description: 'Practical planning playbooks for the anterior situations you actually see.',
    insideLabel: 'Includes',
    inside: [
      'Class IV restorations',
      'Diastema closure',
      'Darkened teeth',
      'Direct composite veneers',
      'Shape modification',
      'Anterior restorations',
      'Composite repairs',
      'Challenging aesthetic situations',
    ],
    footnote: 'ASSESS → DECIDE → PREPARE → BUILD → REFINE → REVIEW',
  },
  {
    number: 'Volume VI',
    title: 'Clinical Finishing & Review System™',
    promise: 'A structured approach to the final stage of your restoration',
    description:
      'The last stage is where cases are won or lost. This volume gives it the same structure as the planning.',
    insideLabel: 'Inside',
    inside: [
      'Finishing',
      'Contour refinement',
      'Anatomy review',
      'Surface texture',
      'Polishing',
      'Visual evaluation',
      'Documentation',
      'Final case review',
      'Post-operative checklist',
      'Case improvement framework',
    ],
  },
]

export const extras = [
  {
    icon: '🧠',
    title: 'Decision Trees',
    text: 'Visual decision maps to help organize your thinking before starting an anterior case.',
  },
  {
    icon: '📊',
    title: 'Clinical Matrices',
    text: 'Quick-reference matrices for case characteristics, material properties, opacity, value and layering.',
  },
  {
    icon: '📋',
    title: 'Chairside Checklists',
    text: 'Practical checklists for case preparation and final review.',
  },
  {
    icon: '🦷',
    title: 'Case Planning Sheets',
    text: 'Printable planning sheets to organize your case before treatment.',
  },
  {
    icon: '🎯',
    title: 'Quick Reference System',
    text: 'The most important concepts condensed into fast-reference resources.',
  },
]

export type Bonus = {
  tag: string
  title: string
  value: string
  text: string
  extra?: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    tag: 'Bonus #1',
    title: '50 Clinical Decision Cards™',
    value: 'Value $37',
    text: '50 visual quick-reference cards covering common decisions in anterior composite cases.',
    extra: 'Designed so you can find a specific reference quickly instead of searching through an entire course.',
    image: 'https://cdn.magicpatterns.com/uploads/meTMyq7nvXoAFwGpSV1ksT/BONUS_1_RESIN-EN.png',
  },
  {
    tag: 'Bonus #2',
    title: 'Anterior Anatomy Cheat Sheet™',
    value: 'Value $27',
    text: 'A visual collection of references covering anterior proportions, line angles, anatomy, surface texture and key morphological features.',
    image: 'https://cdn.magicpatterns.com/uploads/jYJAcVLnTDdiz5ZYawzHki/BONUS_2_RESIN-EN.png',
  },
  {
    tag: 'Bonus #3',
    title: 'Dark Tooth & Challenging Cases Protocols™',
    value: 'Value $37',
    text: 'Visual planning protocols for challenging anterior situations, including darkened teeth, diastema closure, Class IV restorations and composite repairs.',
    image: 'https://cdn.magicpatterns.com/uploads/jkW6188sBr4F4WPaFyJ1gD/BONUS_3_RESIN-EN.png',
  },
  {
    tag: 'Bonus #4',
    title: 'Minimalist Composite Setup™',
    value: 'Value $29',
    text: 'A practical setup guide organized into three levels: ESSENTIAL → EFFICIENT → ADVANCED.',
    extra: 'Includes material categories, instruments and procedure-based setup lists.',
    image: 'https://cdn.magicpatterns.com/uploads/fy669PGVoDePWisaEVTSd5/BONUS_4_RESIN-EN.png',
  },
  {
    tag: 'Bonus #5',
    title: '10 Anterior Case Planning Sheets™',
    value: 'Value $17',
    text: '10 professional planning templates designed to help you organize different anterior composite cases before treatment.',
    image: 'https://cdn.magicpatterns.com/uploads/35zeMvppETEpYmkfgYVQsZ/BONUS_5_RESIN-EN.png',
  },
]

export type Testimonial = {
  name: string
  flag: string
  role: string
  quote: string
  photo: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Maria L.',
    flag: '🇺🇸',
    role: 'General Dentist',
    quote:
      'What I liked most was having everything organized in one place. The matrices make it much easier to find what I’m looking for before starting a case.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/f386dc82-5f9a-446a-9373-d6c7ffd81588.jpg',
  },
  {
    name: 'James R.',
    flag: '🇨🇦',
    role: 'General Dentist',
    quote:
      'The Layering Architecture section completely changed how I think about planning my anterior cases before I start.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/cbc8ed4b-1dd7-44f9-98ed-333c1c402f2e.jpg',
  },
  {
    name: 'Andrea P.',
    flag: '🇬🇧',
    role: 'Dentist',
    quote:
      'It doesn’t feel like another PDF. The Case Playbooks and decision tools make it much more practical to use.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/a8bf4044-88f0-4fee-8a60-add66a3f17cd.jpg',
  },
  {
    name: 'Carlos S.',
    flag: '🇦🇺',
    role: 'Dentist',
    quote: 'The checklists are probably the resource I use the most. They help me review the case before I start.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/d158a473-f6e4-4d4e-9be4-f728e5b888e4.jpg',
  },
  {
    name: 'Lucia M.',
    flag: '🇺🇸',
    role: 'Cosmetic Dentist',
    quote:
      'I didn’t feel like I was buying another textbook. I was buying a system that helps me organize my thinking.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/27f44c88-4c3f-4477-92f7-72c88c0783dd.jpg',
  },
]

export const faqs = [
  {
    q: 'How will I receive Resin Architect™?',
    a: 'Immediately after payment, you’ll receive instructions to access and download the 6 volumes and 5 bonuses in digital format.',
  },
  {
    q: 'Is Resin Architect™ a course?',
    a: 'No. It is a digital clinical reference and planning system containing volumes, matrices, protocols, checklists, case playbooks and planning tools.',
  },
  {
    q: 'Who is Resin Architect™ for?',
    a: 'It is designed for dentists who perform — or want to perform — anterior composite procedures and want a more structured way to organize their case planning.',
  },
  {
    q: 'Do I need to be a specialist?',
    a: 'No. The system is designed as a professional reference tool for dentists working with anterior composite restorations. Your level of experience and clinical scope should guide how you apply the material.',
  },
  {
    q: 'Can I download the files?',
    a: 'Yes. The resources are provided digitally and are designed to be accessible from your computer, tablet or phone.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. You pay $27 USD once and receive lifetime access to the purchased product.',
  },
  {
    q: 'Does this replace clinical education?',
    a: 'No. Resin Architect™ is an educational and reference resource. It does not replace professional education, clinical judgment, patient-specific assessment, applicable regulations or manufacturer instructions.',
  },
  {
    q: 'What if I don’t like it?',
    a: 'You have a 7-day money-back guarantee. Review the material and, if you don’t feel it provides the expected value, you can request a refund within the guarantee period.',
  },
]

export const purchaseNotifications = [
  { name: 'Valentina R.', city: 'Bogotá', country: 'Colombia' },
  { name: 'Diego M.', city: 'Ciudad de México', country: 'México' },
  { name: 'Camila S.', city: 'Buenos Aires', country: 'Argentina' },
  { name: 'Andrés P.', city: 'Lima', country: 'Perú' },
  { name: 'Sofía G.', city: 'Santiago', country: 'Chile' },
  { name: 'Javier T.', city: 'Madrid', country: 'España' },
  { name: 'Daniela C.', city: 'Guayaquil', country: 'Ecuador' },
  { name: 'Mateo L.', city: 'Montevideo', country: 'Uruguay' },
  { name: 'Lucía F.', city: 'San José', country: 'Costa Rica' },
  { name: 'Ricardo V.', city: 'Panamá', country: 'Panamá' },
  { name: 'Paula H.', city: 'Guatemala', country: 'Guatemala' },
  { name: 'Emilio N.', city: 'Asunción', country: 'Paraguay' },
]
